import {
  type AnimationEvent,
  type MouseEvent,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import ErrorIconSvg from '../../../assets/icons/icon-withdrawal-transfer-error.svg?react';
import FeedbackIconSvg from '../../../assets/icons/icon-withdrawal-transfer-feedback.svg?react';
import ProcessingIconSvg from '../../../assets/icons/icon-withdrawal-transfer-processing.svg?react';
import RequestSentIconSvg from '../../../assets/icons/icon-withdrawal-transfer-request-sent.svg?react';
import { zIndexMap } from '../../../shared/config';
import { useSheetPresence } from '../../../shared/hooks/use-sheet-presence';
import { PrimaryButton } from '../../../shared/ui';

const WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS = 240;

export type WithdrawalTransferSheetStatus =
  | 'request-sent'
  | 'processing'
  | 'success'
  | 'failure';

type WithdrawalTransferSheetProps = {
  onOpenSupport?: () => void;
  onClose: () => void;
  status: WithdrawalTransferSheetStatus | null;
};

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const sheetSlideIn = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const sheetSlideOut = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const getOverlayAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${overlayFadeOut} ${WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${overlayFadeIn} 300ms ease-out both;
      `;

const getSheetAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${sheetSlideOut} ${WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${sheetSlideIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      `;

const Overlay = styled.div.attrs({
  'data-ui': 'withdrawal-transfer-overlay',
})<{ $isClosing: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: ${({ theme }) => theme.colors.surface.backdrop};
  overscroll-behavior: contain;
  touch-action: pan-y;
  z-index: ${zIndexMap.withdrawal.overlay};
  ${({ $isClosing }) => getOverlayAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheet = styled.section.attrs({
  'data-ui': 'withdrawal-transfer-sheet',
})<{ $isClosing: boolean }>`
  min-height: ${({ theme }) => theme.scale.px(278)};
  box-sizing: border-box;
  display: grid;
  justify-items: center;
  align-content: start;
  border-top-left-radius: ${({ theme }) => theme.scale.px(21)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(21)};
  padding-top: ${({ theme }) => theme.scale.px(88)};
  padding-right: ${({ theme }) => theme.scale.px(24)};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(40)}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) => theme.scale.px(24)};
  background: ${({ theme }) => theme.colors.surface.base};
  z-index: ${zIndexMap.withdrawal.sheet};
  ${({ $isClosing }) => getSheetAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const StatusTrack = styled.div.attrs({
  'data-ui': 'withdrawal-transfer-status-track',
})`
  width: ${({ theme }) => theme.scale.px(235)};
  height: ${({ theme }) => theme.scale.px(74)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

const Step = styled.div<{ $widthPx: number }>`
  position: relative;
  width: ${({ theme, $widthPx }) => theme.scale.px($widthPx)};
  min-height: ${({ theme }) => theme.scale.px(74)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

const RequestSentIcon = styled(RequestSentIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(24)};
  height: ${({ theme }) => theme.scale.px(23)};
  --stroke-0: ${({ theme }) => theme.colors.support.success};
`;

const ProcessingIcon = styled(ProcessingIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(30)};
  height: ${({ theme }) => theme.scale.px(30)};
  --stroke-0: ${({ theme }) => theme.colors.support.success};
`;

const FeedbackIcon = styled(FeedbackIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(29)};
  height: ${({ theme }) => theme.scale.px(30)};
  --stroke-0: ${({ theme }) => theme.colors.support.success};
`;

const ErrorIcon = styled(ErrorIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(33)};
  height: ${({ theme }) => theme.scale.px(33)};
  --stroke-0: ${({ theme }) => theme.colors.support.dangerSoft};
`;

const RequestLabel = styled.p`
  margin: 0;
  position: absolute;
  top: ${({ theme }) => theme.scale.px(42)};
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  font-family: Inter, sans-serif;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(15)};
  white-space: pre-line;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SupportButton = styled(PrimaryButton)`
  width: ${({ theme }) => theme.scale.px(331)};
  margin-top: ${({ theme }) => theme.scale.px(20)};
`;

export function WithdrawalTransferSheet({
  onOpenSupport,
  onClose,
  status,
}: WithdrawalTransferSheetProps) {
  const { t } = useTranslation();
  const [lastVisibleStatus, setLastVisibleStatus] =
    useState<WithdrawalTransferSheetStatus>('request-sent');
  const resolvedStatus = status ?? lastVisibleStatus;
  const isDismissible =
    resolvedStatus === 'success' || resolvedStatus === 'failure';
  const { handleCloseAnimationEnd, isClosing, isMounted } = useSheetPresence(
    status !== null,
  );

  useEffect(() => {
    if (status !== null) {
      setLastVisibleStatus(status);
    }
  }, [status]);

  const stopOverlayClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  function handleOverlayClick() {
    if (!isDismissible) {
      return;
    }

    onClose();
  }

  const handleSheetAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || !isClosing) {
      return;
    }

    handleCloseAnimationEnd();
  };

  if (!isMounted) {
    return null;
  }

  return (
    <Overlay $isClosing={isClosing} onClick={handleOverlayClick}>
      <Sheet
        $isClosing={isClosing}
        aria-label={t('withdrawal.transferSheetAria')}
        role="dialog"
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={stopOverlayClose}
      >
        <StatusTrack>
          {resolvedStatus === 'request-sent' ? (
            <Step $widthPx={24}>
              <RequestSentIcon />
              <RequestLabel>
                {t('withdrawal.transferSheetRequestSent')}
              </RequestLabel>
            </Step>
          ) : null}

          {resolvedStatus === 'processing' ? (
            <Step $widthPx={30}>
              <ProcessingIcon />
              <RequestLabel>
                {t('withdrawal.transferSheetProcessing')}
              </RequestLabel>
            </Step>
          ) : null}

          {resolvedStatus === 'success' ? (
            <Step $widthPx={33}>
              <FeedbackIcon />
              <RequestLabel>
                {t('withdrawal.transferSheetSuccess')}
              </RequestLabel>
            </Step>
          ) : null}

          {resolvedStatus === 'failure' ? (
            <Step $widthPx={33}>
              <ErrorIcon />
              <RequestLabel>
                {t('withdrawal.transferSheetFailure')}
              </RequestLabel>
            </Step>
          ) : null}
        </StatusTrack>
        {resolvedStatus === 'failure' ? (
          <SupportButton
            type="button"
            onClick={() => {
              onOpenSupport?.();
            }}
          >
            {t('withdrawal.transferSheetSupport')}
          </SupportButton>
        ) : null}
      </Sheet>
    </Overlay>
  );
}
